import { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, LineChart, Line } from 'recharts';
import { dashboard } from '../services/api';

const fmt = (n) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(n || 0);
const fmtExact = (n) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 2 }).format(n || 0);

export default function Reports({ accountId }) {
  const [period, setPeriod] = useState('monthly');
  const [data, setData] = useState(null);
  const [daily, setDaily] = useState([]);
  const [loading, setLoading] = useState(false);

  const periodDays = { daily: 1, weekly: 7, monthly: 30, quarterly: 90,};

  const load = async (p) => {
    if (!accountId) return;
    setLoading(true);
    try {
      const days = periodDays[p];
      const [report, dailyData] = await Promise.all([
        dashboard.summary(accountId, days),
        dashboard.salesDaily(accountId, days),
      ]);
      setData(report);
      setDaily(dailyData.map(r => ({
        ...r,
        date: new Date(r.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }),
        net_profit: parseFloat(r.total_sales) - parseFloat(r.fba_fees || 0) - parseFloat(r.referral_fees || 0) - parseFloat(r.vat || 0),
      })));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handlePeriod = (p) => {
    setPeriod(p);
    load(p);
  };

  const card = { background: '#fff', border: '0.5px solid rgba(0,0,0,0.08)', borderRadius: '12px', padding: '1.25rem', marginBottom: '12px' };

  return (
    <div>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        {['daily', 'weekly', 'monthly', 'quarterly'].map(p => (
          <button key={p} onClick={() => handlePeriod(p)}
            style={{ padding: '8px 16px', borderRadius: '8px', border: '0.5px solid #ddd', background: period === p ? '#1a1a1a' : '#fff', color: period === p ? '#fff' : '#1a1a1a', fontSize: '13px', cursor: 'pointer', textTransform: 'capitalize' }}>
            {p}
          </button>
        ))}
      </div>

      {loading && <div style={{ textAlign: 'center', padding: '3rem', color: '#888' }}>Loading report...</div>}

      {!loading && !data && (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#888' }}>
          Select a period above to view your P&L report
        </div>
      )}

      {!loading && data && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '10px', marginBottom: '1.25rem' }}>
            {[
              { label: 'Gross sales', value: fmtExact(data.total_sales), color: '#1a1a1a' },
              { label: 'Units sold', value: parseInt(data.units_sold).toLocaleString(), color: '#1a1a1a' },
              { label: 'Net profit', value: fmtExact(data.net_profit), color: parseFloat(data.net_profit) >= 0 ? '#1D9E75' : '#E24B4A' },
              { label: 'Margin', value: `${data.margin}%`, color: parseFloat(data.margin) >= 0 ? '#1D9E75' : '#E24B4A' },
            ].map(m => (
              <div key={m.label} style={{ background: '#f0efe9', borderRadius: '10px', padding: '14px 16px', border: '0.5px solid rgba(0,0,0,0.06)' }}>
                <div style={{ fontSize: '11px', color: '#777', marginBottom: '5px', fontWeight: '500' }}>{m.label}</div>
                <div style={{ fontSize: '20px', fontWeight: '500', color: m.color }}>{m.value}</div>
              </div>
            ))}
          </div>

          <div style={card}>
            <div style={{ fontSize: '13px', fontWeight: '500', marginBottom: '1rem' }}>P&L Breakdown</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <tbody>
                {[
                  { label: 'Gross sales', value: data.total_sales, color: '#1D9E75' },
                  { label: 'FBA fees', value: -data.fba_fees, color: '#E24B4A' },
                  { label: 'Referral fees', value: -data.referral_fees, color: '#E24B4A' },
                  { label: 'VAT collected', value: -data.vat, color: '#E24B4A' },
                  { label: 'Advertising spend', value: -data.advertising_spend, color: '#E24B4A' },
                  { label: 'COGS', value: -data.cogs, color: '#E24B4A' },
                  { label: 'NET PROFIT', value: data.net_profit, color: parseFloat(data.net_profit) >= 0 ? '#1D9E75' : '#E24B4A', bold: true },
                ].map((row, i) => (
                  <tr key={i} style={{ borderBottom: '0.5px solid rgba(0,0,0,0.05)' }}>
                    <td style={{ padding: '10px 0', fontWeight: row.bold ? '600' : '400', fontSize: row.bold ? '14px' : '13px' }}>{row.label}</td>
                    <td style={{ padding: '10px 0', textAlign: 'right', color: row.color, fontWeight: row.bold ? '600' : '400', fontSize: row.bold ? '14px' : '13px' }}>
                      {parseFloat(row.value) >= 0 ? '+' : ''}{fmtExact(row.value)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {daily.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div style={card}>
                <div style={{ fontSize: '12px', fontWeight: '500', color: '#777', marginBottom: '12px' }}>Sales over period</div>
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={daily}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `£${v}`} />
                    <Tooltip formatter={v => fmt(v)} />
                    <Bar dataKey="total_sales" fill="#378ADD" radius={[3, 3, 0, 0]} name="Sales" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div style={card}>
                <div style={{ fontSize: '12px', fontWeight: '500', color: '#777', marginBottom: '12px' }}>Daily net profit</div>
                <ResponsiveContainer width="100%" height={180}>
                  <LineChart data={daily}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `£${v}`} />
                    <Tooltip formatter={v => fmt(v)} />
                    <Line type="monotone" dataKey="net_profit" stroke="#1D9E75" strokeWidth={2} dot={false} name="Net profit" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}